<template>
  <!-- Full-screen lockout state (issue 213 F23/F24/F26). Shown by AppChrome in place
       of the workspace when the active tenant is suspended/blocked (the payments
       ladder's lockout). A suspended tenant's module data 404s and /billing 403s, so
       the old experience was dead tiles + cryptic "Eintrag nicht gefunden" toasts and
       a silent Abrechnung bounce home; this screen owns that surface instead.

       Recovery is SUPPORT-CONTACT ONLY: in-app self-service pay-to-restore is
       intentionally NOT offered here. payment-api resolves a suspended tenant to null
       (resolveTenantIdBySlug), so the card/mandate-setup routes 403 by design — letting
       the pay flow through the lockout needs a backend change and is deferred to a
       separate follow-up. So the way back is to contact support, who can lift the
       suspension (or send a payment link). -->
  <div class="ws-locked">
    <div class="ws-locked__card">
      <LumiIcon name="privacy" class="ws-locked__icon" aria-hidden="true" />
      <h1 class="ws-locked__title">{{ t('billing.banner.suspendedTitle') }}</h1>
      <p class="ws-locked__body">{{ t('billing.banner.lockedBody') }}</p>
      <p class="ws-locked__lead">{{ t('billing.banner.lockedSupportLead') }}</p>
      <a class="ws-locked__cta" :href="supportMailto">{{ t('billing.banner.lockedSupportCta') }}</a>
      <a class="ws-locked__email" :href="supportMailto">{{ SUPPORT_EMAIL }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LumiIcon } from '@lumi/ui';

// The support address the product already publishes (landing-ui footer /
// SharedFooter). Reused verbatim so a locked-out member reaches the same inbox —
// an address, not translatable copy, so it lives here rather than in i18n.
const SUPPORT_EMAIL = 'support@lumi-hr.de';

const { t } = useI18n();
const supportMailto = computed(() => `mailto:${SUPPORT_EMAIL}`);
</script>

<style scoped>
.ws-locked {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 24px 16px;
}
.ws-locked__card {
  max-width: 520px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: var(--surface-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: 40px 32px;
  box-shadow: var(--shadow-md);
}
.ws-locked__icon {
  width: 44px;
  height: 44px;
  color: var(--text-muted);
}
.ws-locked__title {
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text-title);
}
.ws-locked__body {
  color: var(--text-muted);
  line-height: 1.5;
}
.ws-locked__lead {
  color: var(--text-body);
  margin-top: 8px;
}
.ws-locked__cta {
  display: inline-block;
  margin-top: 4px;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  background: var(--accent-soft);
  color: var(--accent-text);
  font-weight: 600;
  text-decoration: none;
}
.ws-locked__cta:hover {
  background: var(--accent-soft-2);
}
.ws-locked__email {
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-decoration: underline;
}
</style>
